import { FieldSlugEnum } from 'common/enums/FieldSlugEnum'
import { BadRequest } from 'common/errors'
import { optInFail } from 'publisher/actions/optInActions'
import { calculateOfferPricing } from 'publisher/pages/offer-page/utils/calculateOfferPricing'
import { useOptIn, usePayment } from 'publisher/store'
import { getFieldValueBySlug } from 'publisher/store/optIn/optInSelectors'
import { setOfferPricing } from 'publisher/store/payment/paymentActions'
import {
  getActivePaymentMethod,
  getCheckedBump,
  getCheckedCoupon,
  getCouponCode,
  getOfferId,
  getProduct,
  getProductActiveVariant,
  getProductQuantity,
  getSelectedShipping,
} from 'publisher/store/payment/paymentSelectors'
import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useCalculatedPricePlans } from 'src/publisher/hooks/use-calculated-price-plans'

/**
 * Triggers the offer pricing API fetch and writes results to the Redux store.
 * Call this only once per page — in the offer price component that owns the fetch.
 */
export const useFetchOfferPricing = () => {
  const dispatch = useDispatch()
  const abortControllerRef = useRef<AbortController>()

  const offerId = usePayment(getOfferId)
  const couponCode = usePayment(getCouponCode)
  const checkedCoupon = usePayment(getCheckedCoupon)
  const bump = usePayment(getCheckedBump)
  const activePaymentMethod = usePayment(getActivePaymentMethod)

  const { pricePlans } = useCalculatedPricePlans()
  const bumpPlan = bump?.pricePlans[0]

  const taxNumber = useOptIn(state =>
    getFieldValueBySlug(state, FieldSlugEnum.TaxNumber),
  )
  const customerCountry = useOptIn(state =>
    getFieldValueBySlug(state, FieldSlugEnum.Country),
  )
  const product = usePayment(getProduct)
  const activeProductVariant = usePayment(getProductActiveVariant)
  const productQuantity = usePayment(getProductQuantity)
  const productBump = bump?.product || null
  const selectedShipping = usePayment(getSelectedShipping)

  // We intentionally use specific ids in the dependency array rather than
  // whole objects to avoid triggering re-renders on reference inequality.
  // biome-ignore lint/correctness/useExhaustiveDependencies: uses obj ids for deps check
  useEffect(() => {
    if (!offerId || (!product?.id && !pricePlans.length)) {
      return
    }

    const fetchPriceData = async () => {
      try {
        abortControllerRef.current = new AbortController()

        const newOfferPricings = await calculateOfferPricing({
          product,
          productBump,
          pricePlans,
          bumpPlan,
          bumpOfferId: bump?.id,
          offerId,
          country: customerCountry,
          activeProductVariant,
          couponCode: checkedCoupon ? couponCode : undefined,
          taxNumber,
          productQuantity,
          paymentMethod: activePaymentMethod,
          shippingRule: selectedShipping?.ruleId,
          shippingCondition: selectedShipping?.conditionId,
          abortSignal: abortControllerRef.current.signal,
        })
        dispatch(setOfferPricing(newOfferPricings))
      } catch (error) {
        if (error instanceof BadRequest) {
          dispatch(optInFail({ fields: error.response.data.errors.fields }))
        }
      }
    }

    if (abortControllerRef.current?.signal) {
      abortControllerRef.current.abort('new_request_sent')
    }

    fetchPriceData()
  }, [
    product?.id,
    productQuantity,
    bump?.id,
    activeProductVariant,
    couponCode,
    checkedCoupon?.id,
    selectedShipping,
    customerCountry,
    offerId,
    taxNumber,
    activePaymentMethod,
  ])
}
